import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Logo } from "@/components/brand/logo";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { noIndexMeta } from "@/lib/seo";

export const Route = createFileRoute("/cli-auth")({
	component: CliAuthPage,
	head: () => ({
		meta: [{ title: "AgentReacher | CLI authorization" }, ...noIndexMeta()],
	}),
});

type AuthState = "loading" | "authorizing" | "success" | "error";

function CliAuthPage() {
	const { session, user, isPending } = useSession();
	const { isActive, isLoading: isSubscriptionLoading } = useSubscription();
	const [authState, setAuthState] = useState<AuthState>("loading");
	const [error, setError] = useState<string | null>(null);
	const [countdown, setCountdown] = useState(3);

	const code =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search).get("code")
			: null;
	const needsCheckout = !isActive;

	const authorize = useCallback(async () => {
		if (!code) {
			setError("Missing authorization code.");
			setAuthState("error");
			return;
		}

		setAuthState("authorizing");

		try {
			const response = await fetch("/api/mcp/cli-authorize", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ code }),
			});

			const payload = await response.json().catch(() => null);

			if (!response.ok) {
				setError(payload?.error || "Failed to authorize.");
				setAuthState("error");
				return;
			}

			setAuthState("success");
		} catch {
			setError("Something went wrong. Please try again.");
			setAuthState("error");
		}
	}, [code]);

	useEffect(() => {
		if (isPending) return;

		if (!user || !session) {
			const redirectTarget = `/cli-auth?code=${code || ""}`;
			const redirectUrl = `/signup?redirect=${encodeURIComponent(redirectTarget)}`;
			window.location.assign(redirectUrl);
			return;
		}

		if (isSubscriptionLoading) return;

		if (needsCheckout) {
			const redirectTarget = `/cli-auth?code=${code || ""}`;
			const redirectUrl = `/choose-plan?redirect=${encodeURIComponent(redirectTarget)}`;
			window.location.assign(redirectUrl);
			return;
		}

		if (authState === "loading" && code) {
			authorize();
		}
	}, [
		isPending,
		user,
		session,
		isSubscriptionLoading,
		needsCheckout,
		authState,
		code,
		authorize,
	]);

	useEffect(() => {
		if (authState !== "success") return;
		if (countdown <= 0) {
			window.location.assign("/dashboard");
			return;
		}
		const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [authState, countdown]);

	if (isPending || (!!user && isSubscriptionLoading)) return <LoadingScreen />;

	if (!code) {
		return (
			<AuthLayout>
				<StatusIcon state="error" />
				<h1 className="text-2xl font-semibold tracking-[-0.04em]">
					Invalid link
				</h1>
				<p className="text-md text-muted-foreground">
					This authorization link is missing a code. Run{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">
						npx agentreacher@latest init
					</code>{" "}
					again.
				</p>
			</AuthLayout>
		);
	}

	if (authState === "loading" || authState === "authorizing") {
		return (
			<AuthLayout>
				<StatusIcon state="authorizing" />
				<h1 className="text-2xl font-semibold tracking-[-0.04em]">
					Connecting your agent…
				</h1>
				<p className="text-md text-muted-foreground">
					Generating your API key and linking it to your account.
				</p>
			</AuthLayout>
		);
	}

	if (authState === "error") {
		return (
			<AuthLayout>
				<StatusIcon state="error" />
				<h1 className="text-2xl font-semibold tracking-[-0.04em]">
					Authorization failed
				</h1>
				<p className="text-md text-muted-foreground">
					{error || "Something went wrong."}
				</p>
				<p className="text-sm text-muted-foreground">
					Run{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
						npx agentreacher@latest init
					</code>{" "}
					to try again.
				</p>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout>
			<StatusIcon state="success" />
			<h1 className="text-2xl font-semibold tracking-[-0.04em]">
				Agent connected
			</h1>
			<p className="text-md text-muted-foreground">
				Your CLI has been authorized. Redirecting to dashboard in {countdown}s…
			</p>
			<Link
				to="/dashboard"
				className="text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
			>
				Go to dashboard now →
			</Link>
		</AuthLayout>
	);
}

function AuthLayout({ children }: { children: React.ReactNode }) {
	return (
		<div className="min-h-svh bg-background text-foreground">
			<main className="flex min-h-svh items-center justify-center px-4 sm:px-6">
				<div className="w-full max-w-lg space-y-6">
					<Logo size="sm" />
					<div className="space-y-3">{children}</div>
				</div>
			</main>
		</div>
	);
}

function StatusIcon({ state }: { state: AuthState }) {
	if (state === "success") {
		return (
			<div className="flex size-10 items-center justify-center rounded-full bg-emerald-500/10">
				<Check className="size-5 text-emerald-500" />
			</div>
		);
	}
	if (state === "error") {
		return (
			<div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
				<X className="size-5 text-destructive" />
			</div>
		);
	}
	return (
		<div className="flex size-10 items-center justify-center rounded-full bg-muted">
			<Loader2 className="size-5 animate-spin text-muted-foreground" />
		</div>
	);
}
